import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** Compact "what's next" panel shared across every role's dashboard —
 * /enrollments/events/ is already scoped server-side per role (a student's
 * own enrolled-module events, a teacher's own modules, a parent's
 * children's, everything for admin), so this needs no role branching. */
export default function UpcomingEvents({ events, loading }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const upcoming = (events || [])
    .filter((e) => new Date(e.start_datetime) >= new Date())
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
    .slice(0, 4);

  return (
    <div className="bg-white rounded-2xl shadow-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-display font-bold text-gray-800">{t('Upcoming')}</h3>
        <button onClick={() => navigate('/calendar')} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
          {t('View Calendar')}
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-400">{t('Loading...')}</div>
      ) : upcoming.length === 0 ? (
        <div className="py-6 text-center">
          <svg className="w-8 h-8 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm text-gray-400">{t('Nothing scheduled')}</p>
        </div>
      ) : (
        <div className="space-y-1">
          {upcoming.map((event) => {
            const date = new Date(event.start_datetime);
            return (
              <button
                key={event.id}
                onClick={() => navigate('/calendar')}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition text-left"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-primary-50 flex flex-col items-center justify-center leading-none">
                  <span className="text-[9px] font-semibold text-primary-500 uppercase">{date.toLocaleDateString(undefined, { month: 'short' })}</span>
                  <span className="text-sm font-bold text-primary-700">{date.getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{event.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {event.module_name} • {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
